import React, { useState, useEffect } from "react";
import { Check, Users, Database, Play } from "lucide-react";
import { useSelector } from "react-redux";
import { plans } from "../constants";
import axiosAPI from "../config/axiosInstance";
import { Navigate } from "react-router-dom";

const Account = () => {
  const { user } = useSelector((state) => state.user);
  const subscription = user.companyData?.subscription;
  const currentPlan = subscription ? subscription?.plan : "free";
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get("success")) {
      setSuccess(true);
      setSessionId(query.get("session_id"));
    }
    if (query.get("canceled")) {
      setSuccess(false);
      setMessage("Order canceled. Please try again when you're ready.");
    }
  }, []);

  const handleCheckout = async (lkupkey, planId) => {
    try {
      setLoadingPlanId(planId);
      const response = await axiosAPI("POST", "create-checkout-session", {
        lookup_key: lkupkey,
      });

      if (response.url) {
        window.location.href = response.url;
      } else {
        setMessage("Failed to redirect to payment page. Please try again.");
      }
    } catch (error) {
      setMessage(
        error.error ||
          "An error occurred while creating checkout session. Please try again.",
      );
    } finally {
      setLoadingPlanId(null);
    }
  };

  const FeatureRow = ({ icon, label, value, highlight = false }) => (
    <div
      className={`flex items-center justify-between py-2 lg:py-3 px-3 lg:px-4 rounded-lg ${
        highlight ? "bg-blue-50" : "bg-gray-50"
      }`}
    >
      <div className="flex items-center space-x-2 lg:space-x-3">
        {icon}
        <span className="text-xs lg:text-sm font-medium text-gray-700">
          {label}
        </span>
      </div>
      <span
        className={`text-xs lg:text-sm font-semibold ${
          highlight ? "text-blue-600" : "text-gray-900"
        }`}
      >
        {typeof value === "boolean" ? (value ? "Yes" : "No") : value}
      </span>
    </div>
  );
  const handleManageBilling = async () => {
    try {
      setBillingLoading(true);

      const response = await axiosAPI("POST", "create-checkout-session", {
        lookup_key: "manage",
      });

      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      setMessage(
        error.error ||
          "An error occurred while managing billing. Please try again.",
      );
    } finally {
      setBillingLoading(false);
    }
  };

  const SuccessDisplay = ({ sessionId }) => (
    <section className="text-center p-8">
      <h2 className="text-2xl font-bold text-green-600 mb-4">
        Subscription Successful 🎉
      </h2>
      <p className="text-gray-700 mb-6">
        Your subscription has been activated. Thank you for upgrading!
      </p>
      <input type="hidden" name="session_id" value={sessionId} />
      <button
        type="button"
        onClick={handleManageBilling}
        disabled={billingLoading}
        className={`bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-lg ${
          billingLoading ? "opacity-70 cursor-not-allowed" : ""
        }`}
      >
        {billingLoading ? (
          <span className="flex items-center justify-center space-x-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            <span>Redirecting...</span>
          </span>
        ) : (
          "Manage Billing & Subscriptions"
        )}
      </button>
      <button
        onClick={resetView}
        className="ml-4 bg-gray-600 hover:bg-gray-500 text-white px-6 py-3 rounded-lg"
      >
        Back to Plans
      </button>
    </section>
  );

  const FailureDisplay = ({ message }) => (
    <section className="text-center p-8">
      <h2 className="text-2xl font-bold text-red-600 mb-4">
        Something went wrong 😞
      </h2>
      <p className="text-gray-700">{message}</p>
      <button
        onClick={() => window.location.reload()}
        className="mt-4 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg"
      >
        Try again
      </button>
    </section>
  );

  const getButtonText = (current, plan) => {
    if (current === plan.id) {
      return "Manage Plan";
    } else if (current === "premium" && plan.id === "basic") {
      return "Downgrade To Basic Plan";
    } else {
      return plan.buttonText;
    }
  };
  const resetView = () => {
    setSuccess(false);
    setSessionId("");
    setMessage("");
  };
  if (success && sessionId) {
    return <SuccessDisplay sessionId={sessionId} />;
  } else if (message) {
    return <FailureDisplay message={message} />;
  }

  if (user?.role !== "super_admin") {
    return <Navigate to="/portal/dashboard" replace />;
  }

  return (
    <div className="w-full h-full overflow-y-auto bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 lg:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Scale your workflow with the right plan for your team. Upgrade or
            downgrade at any time.
          </p>
        </div>

        {/* Current Plan Indicator */}
        {currentPlan && (
          <div className="mb-6 lg:mb-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm sm:text-base">
              <Check className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium">
                Currently on {plans.find((p) => p.id === currentPlan)?.name}{" "}
                plan
              </span>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-xl lg:rounded-2xl shadow-lg lg:shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                plan.popular
                  ? "ring-2 lg:ring-4 ring-blue-500 ring-opacity-50"
                  : ""
              } ${
                currentPlan === plan.id
                  ? "ring-2 lg:ring-4 ring-green-500 ring-opacity-50"
                  : ""
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white px-2 py-1 lg:px-3 lg:py-1 rounded-bl-lg text-xs sm:text-sm font-semibold">
                  Most Popular
                </div>
              )}

              {/* Current Plan Badge */}
              {currentPlan === plan.id && (
                <div className="absolute top-0 left-0 bg-green-500 text-white px-2 py-1 lg:px-3 lg:py-1 rounded-br-lg text-xs sm:text-sm font-semibold flex items-center space-x-1">
                  <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Current</span>
                </div>
              )}

              {/* Card Header */}
              <div className="p-6 lg:p-8 text-center">
                <div className="flex justify-center mb-3 lg:mb-4 text-blue-600">
                  {plan.icon}
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm lg:text-base text-gray-600 mb-4">
                  {plan.description}
                </p>
                <div className="mb-4 lg:mb-6">
                  <span className="text-3xl lg:text-5xl font-bold text-gray-900">
                    {plan.price}
                  </span>
                  <span className="text-sm lg:text-base text-gray-600 ml-2">
                    /{plan.period}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="px-4 lg:px-6 pb-6 lg:pb-8 space-y-2">
                <FeatureRow
                  icon={<Users className="w-5 h-5 text-blue-600" />}
                  label="Number of Users"
                  value={plan.features.users}
                />
                <FeatureRow
                  icon={<Database className="w-5 h-5 text-green-600" />}
                  label="Save Mappings"
                  value={plan.features.saveMappings}
                  highlight={plan.features.saveMappings}
                />
                <FeatureRow
                  icon={<Database className="w-5 h-5 text-purple-600" />}
                  label="Mappings Storage"
                  value={plan.features.mappingsToStore}
                />
                <FeatureRow
                  icon={<Play className="w-5 h-5 text-orange-600" />}
                  label="Executions"
                  value={plan.features.executions}
                />
              </div>

              {/* CTA Button */}
              {plan.id === "free" ? (
                <div className="px-4 lg:px-6 pb-6 lg:pb-8"></div>
              ) : (
                <div className="px-4 lg:px-6 pb-6 lg:pb-8">
                  <button
                    className={`w-full py-3 lg:py-4 px-4 lg:px-6 rounded-lg lg:rounded-xl font-semibold text-sm lg:text-base transition-all duration-200  transform hover:scale-105 shadow-lg ${
                      plan.buttonStyle
                    } 
                    ${loadingPlanId === plan.id ? "opacity-70 cursor-not-allowed" : ""}
                    `}
                    onClick={() => handleCheckout(plan.lookupKey, plan.id)}
                  >
                    {loadingPlanId === plan.id ? (
                      <span className="flex items-center justify-center space-x-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        <span>Processing...</span>
                      </span>
                    ) : (
                      getButtonText(currentPlan, plan)
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 lg:mt-16 text-center">
          <div className="bg-white rounded-lg lg:rounded-xl shadow-lg p-6 lg:p-8 max-w-3xl mx-auto">
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 lg:mb-4">
              Need a custom plan?
            </h3>
            <p className="text-sm lg:text-base text-gray-600 mb-4 lg:mb-6 px-4">
              Contact our sales team for enterprise solutions with custom user
              limits, dedicated support, and advanced security features.
            </p>
            <button className="bg-gray-900 hover:bg-gray-800 text-white px-6 lg:px-8 py-3 rounded-lg font-semibold text-sm lg:text-base transition-colors">
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
